import { BLOCK_REUSABLE_CONTENT } from '@gitbook/doc-core';
import { Block, BlockRange } from '@gitbook/slate';
import { getEventTransfer, type Plugin } from '@gitbook/slate-react';
import { useAPI } from '@gitbook/util-react-hooks';

import type { ContentEditorContextValue } from '../contexts';
import { GITBOOK_CONTEXT_CONTENT_TYPE } from '../types';
import { useSetDisclaimer } from './ReusableContentCopyDisclaimer';

export function usePlugin(editorContext: ContentEditorContextValue): Plugin {
    const setDisclaimer = useSetDisclaimer();

    return useAPI({
        onCopy: (_event, change) => {
            const selection = change.value.selection;
            if (
                !BlockRange.isBlockRange(selection) ||
                !selection.anchorBlock ||
                !selection.focusBlock
            ) {
                return;
            }
            const isForward = change.value.document.areDescendantsSorted(
                selection.anchorBlock,
                selection.focusBlock
            );

            const endKey = isForward ? selection.focusBlock : selection.anchorBlock;

            setDisclaimer(endKey);
        },

        onPaste: (event, change) => {
            const transfer = getEventTransfer(event);

            if (transfer.type !== 'fragment' || !transfer.fragment) {
                return;
            }

            const currentContext = editorContext.copyPaste.getContext();
            const context = editorContext.copyPaste.parseContext(
                transfer.others[GITBOOK_CONTEXT_CONTENT_TYPE]
            );
            let fragment = transfer.fragment;
            let dirty = false;

            fragment = fragment.mapDescendants((node) => {
                if (!Block.isBlock(node) || node.type !== BLOCK_REUSABLE_CONTENT) {
                    return node;
                }

                const incomingRef = node.data.get('ref');

                // if it's a relative ref, and we're pasting in a different space, we need to set the space
                if (!incomingRef.space && context.space !== currentContext.space) {
                    dirty = true;
                    const ref = node.data.get('ref');
                    ref.space = context.space;
                    return node.setData(['ref'], ref);
                }

                return node;
            });

            if (dirty) {
                change.insertDocument(fragment);
                return true;
            }
        },
    });
}
